import {
  DSBadge,
  DSDropdown,
  DSInput,
} from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms";
import {
  faGripVertical,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth, useTeam } from "@hooks";
import { useRef, useState } from "react";

import { GROUP_SHARE_OPTIONS } from "./constants";

const TemplateGroupForm = ({
  onClose,
  onSave,
  saving = false,
  initialName = "",
  initialTemplates = [],
  initialSharingType = "PRIVATE",
  availableTemplates = [],
  isEditing = false,
}) => {
  const [name, setName] = useState(initialName);
  const [templates, setTemplates] = useState(initialTemplates);
  const [sharingType, setSharingType] = useState(initialSharingType);
  const [nameError, setNameError] = useState("");
  const [draggingIndex, setDraggingIndex] = useState(null);
  const dragIndexRef = useRef(null);
  const { userTeams } = useTeam();
  const { userUuid } = useAuth();

  // A template is compatible with a TEAM group if:
  // - PUBLIC (always OK)
  // - TEAM and owned by the user (third-party TEAM templates are not safe)
  // PRIVATE templates are never compatible — they must be shared first.
  const isCompatibleWithTeamGroup = (t) => {
    if (t.sharingType === "PUBLIC") return true;
    if (t.sharingType === "PRIVATE") return false;
    // TEAM template — only OK if user owns it
    return t.user?.uuid === userUuid;
  };

  const addedUuids = new Set(templates.map((t) => t.uuid));
  const dropdownOptions = availableTemplates.filter((t) => {
    if (addedUuids.has(t.uuid)) return false;
    if (sharingType === "TEAM" && !isCompatibleWithTeamGroup(t))
      return false;
    return true;
  });

  const handleAddTemplate = (_value, option) => {
    setTemplates((prev) => [...prev, option]);
  };

  const handleRemoveTemplate = (uuid) => {
    setTemplates((prev) => prev.filter((t) => t.uuid !== uuid));
  };

  const handleDragStart = (index) => {
    dragIndexRef.current = index;
    setDraggingIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === null || from === index) return;

    setTemplates((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(from, 1);
      updated.splice(index, 0, moved);
      return updated;
    });
    dragIndexRef.current = index;
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDraggingIndex(null);
  };

  const availableSharingOptions =
    !userTeams || userTeams.length === 0
      ? GROUP_SHARE_OPTIONS.filter((o) => o.value !== "TEAM")
      : GROUP_SHARE_OPTIONS;

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError("Group name is required");
      return;
    }
    setNameError("");
    onSave({
      name: trimmedName,
      templateUuids: templates.map((t) => t.uuid),
      sharingType,
    });
  };

  const incompatibleTemplatesInTeamGroup =
    sharingType === "TEAM"
      ? templates.filter((t) => !isCompatibleWithTeamGroup(t))
      : [];

  const canSave =
    name.trim().length > 0 &&
    templates.length >= 2 &&
    incompatibleTemplatesInTeamGroup.length === 0;

  return (
    <DSModal
      isOpen={true}
      onClose={onClose}
      title={
        isEditing ? "Edit Template Group" : "Create Template Group"
      }
      actions={[
        { label: "Cancel", variant: "cancel", onClick: onClose },
        {
          label: saving ? "Saving..." : "Save",
          variant: "primary",
          onClick: handleSave,
          disabled: !canSave || saving,
          dataTestId: "save-template-group-button",
        },
      ]}
      subtitle="Template Groups allow you to create multiple notes based on the same appointment audio. For example, you can group together a SOAP, client summary, and follow-up to-do list all in one go!"
      width="w-full max-w-[600px]"
      dataTestId="template-group-modal"
    >
      <div className="flex flex-col gap-md">
        <DSInput
          label="Group Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError("");
          }}
          placeholder="e.g., Standard Exam"
          error={nameError}
          dataTestId="template-group-name-input"
          autoFocus
        />

        <div>
          <DSDropdown
            options={dropdownOptions}
            onChange={handleAddTemplate}
            placeholder="Add a template..."
            search
            searchPlaceholder="Search templates..."
            width="w-full"
            maxHeight={300}
            dataTestId="template-group-add-dropdown"
            getOptionLabel={(opt) => opt.name || ""}
            getOptionValue={(opt) => opt.uuid || ""}
            value={null}
            label="Add Templates"
          />
        </div>

        <div>
          <p className="text-xs text-secondary mb-sm">
            Templates ({templates.length})
            {templates.length > 1 ? " — drag to reorder" : ""}
          </p>

          {templates.length === 0 && (
            <p className="text-sm text-secondary py-md text-center">
              Search and add templates above.
            </p>
          )}

          <div className="flex flex-col gap-xs">
            {templates.map((template, index) => (
              <div
                key={template.uuid}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-sm p-sm rounded-standard bg-secondary transition-all ${draggingIndex === index ? "opacity-50" : "opacity-100"}`}
                data-testid={`template-group-item-${index}`}
              >
                <FontAwesomeIcon
                  icon={faGripVertical}
                  className="text-secondary cursor-grab shrink-0"
                />
                <span className="text-sm font-medium truncate flex-1 min-w-0">
                  {template.name}
                </span>
                <DSBadge
                  variant={
                    template.templateType === "SOAP"
                      ? "brand"
                      : "blue"
                  }
                  size="sm"
                  dataTestId={`template-group-item-badge-${index}`}
                >
                  {template.templateType === "SOAP"
                    ? "SOAP"
                    : "Freeform"}
                </DSBadge>
                <button
                  className="text-secondary hover:text-danger transition-colors p-xs cursor-pointer"
                  onClick={() => handleRemoveTemplate(template.uuid)}
                  data-testid={`template-group-remove-${index}`}
                >
                  <FontAwesomeIcon icon={faTrash} size="sm" />
                </button>
              </div>
            ))}
          </div>

          {templates.length > 0 && templates.length < 2 && (
            <p className="text-xs text-danger mt-xs">
              A group must have at least 2 templates.
            </p>
          )}
        </div>

        <div>
          <p className="text-xs text-secondary mb-sm">Sharing</p>
          <div className="space-y-1">
            {availableSharingOptions.map((option) => (
              <div
                key={option.value}
                onClick={() => setSharingType(option.value)}
                className={`flex-1 ${
                  sharingType === option.value && "bg-brand-subtle"
                } hover:bg-secondary rounded-large p-sm cursor-pointer transition-colors`}
                data-testid={`template-group-sharing-${option.value}`}
              >
                <div className="flex flex-row items-center space-x-3">
                  <input
                    onChange={() => setSharingType(option.value)}
                    checked={sharingType === option.value}
                    type="radio"
                    value={option.value}
                    className="text-brand focus:ring-0"
                  />
                  <FontAwesomeIcon
                    icon={option.icon}
                    className="text-brand"
                    size="lg"
                  />
                  <div className="flex flex-col flex-1">
                    <h5
                      className={`font-semibold cursor-pointer ${
                        sharingType === option.value && "text-brand"
                      }`}
                    >
                      {option.title}
                    </h5>
                    <h5 className="text-xs text-secondary">
                      {option.description}
                    </h5>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {incompatibleTemplatesInTeamGroup.length > 0 && (
            <p className="text-xs text-danger mt-xs">
              {(() => {
                const hasPrivate =
                  incompatibleTemplatesInTeamGroup.some(
                    (t) => t.sharingType === "PRIVATE",
                  );
                const hasThirdParty =
                  incompatibleTemplatesInTeamGroup.some(
                    (t) =>
                      t.sharingType !== "PRIVATE" &&
                      t.user?.uuid !== userUuid,
                  );
                if (hasPrivate && hasThirdParty)
                  return "Some templates are private or owned by another user and can't be used in a shared group.";
                if (hasPrivate)
                  return "Some templates are private and must be shared before they can be used in a shared group.";
                return "Some templates are owned by another user and can't be included in a shared group.";
              })()}{" "}
              Remove them or switch back to &quot;Private&quot;:{" "}
              {incompatibleTemplatesInTeamGroup
                .map((t) => t.name)
                .join(", ")}
            </p>
          )}
        </div>
      </div>
    </DSModal>
  );
};

const TemplateGroupModal = ({ isOpen, ...props }) => {
  if (!isOpen) return null;
  return <TemplateGroupForm {...props} />;
};

export default TemplateGroupModal;
